import { Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { filter, map, tap } from 'rxjs';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { Meta } from '@angular/platform-browser';
import { BaseComponent } from './~shared/abstract-components/base.component';
import {
  CSS_TAG_LIST,
  SCRIPT_TAG_LIST
} from './~shared/constants/global.constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LoadingBarModule, LoadingBarHttpClientModule, RouterOutlet]
})
export class AppComponent extends BaseComponent implements OnInit {
  @HostListener('window:online', ['$event'])
  onOnlineEvent(): void {
    this.setOnlineStatus(true);
  }

  @HostListener('window:offline', ['$event'])
  onOfflineEvent(): void {
    this.setOnlineStatus(false);
  }

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _metaService: Meta,
    private _renderer: Renderer2
  ) {
    super();
  }

  ngOnInit() {
    this.addMetaTags();
    if (this._isPlatformBrowser) {
      this.addRequiredHtmlElements();
      this.setOnlineStatus();
      this.listenToRouteParamsChanges();
    }
  }

  listenToRouteParamsChanges(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event instanceof NavigationEnd),
        map(() => this._activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        tap(activatedRoute => {
          const { snapshot } = activatedRoute;
          const params = this._getActivatedRouteParams(activatedRoute);
          const routeData = this._globalFacade.routeData;

          this._globalFacade.setRouteData({
            params: params,
            queryParams: snapshot.queryParams,
            currentUrl: this._router.url,
            currentUrlWithoutParams: this._router.url.split('?')[0],
            previousUrl: routeData?.currentUrl || '/',
            data: snapshot.data
          });
          this.setDynamicTags();
        })
      )
      .subscribe();
  }

  setOnlineStatus(status?: boolean): void {
    this._globalFacade.setOnlineStatus(
      status === undefined ? navigator.onLine : status
    );
  }

  addMetaTags(): void {
    this._metaService.updateTag({
      property: 'og:url',
      content: this.environment.baseUrl
    });
    this._metaService.updateTag({
      property: 'og:type',
      content: 'website'
    });
    this._metaService.updateTag({
      property: 'og:description',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:site_name',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:image',
      content: `${this.environment.baseUrl}/assets/icons/logo.png`
    });

    // this._metaService.updateTag({
    //   property: 'twitter:image',
    //   content: `${environment.baseUrl}/assets/icons/logo.png`
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:card',
    //   content: 'summary_large_image'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:site',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:creator',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:title',
    //   content: 'app'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:description',
    //   content: 'app'
    // });
  }

  addRequiredHtmlElements(): void {
    for (const cssEl of CSS_TAG_LIST) {
      const linkTag = this._renderer.createElement('link');
      this._renderer.setAttribute(linkTag, 'href', cssEl.src);
      if (cssEl.attributes) {
        for (const attr of cssEl.attributes) {
          this._renderer.setAttribute(linkTag, attr.key, attr.value || '');
        }
      }
      this._renderer.appendChild(document.head, linkTag);
    }

    for (const scriptEl of SCRIPT_TAG_LIST) {
      const scriptTag = this._renderer.createElement('script');
      this._renderer.setAttribute(scriptTag, 'src', scriptEl.src);
      if (scriptEl.attributes) {
        for (const attr of scriptEl.attributes) {
          this._renderer.setAttribute(scriptTag, attr.key, attr.value || '');
        }
      }
      this._renderer.appendChild(document.body, scriptTag);
    }
  }

  setDynamicTags(): void {
    //  TODO: set dynamic tags
  }

  private _getActivatedRouteParams(
    route: ActivatedRoute
  ): Record<string, string> {
    const allParams = { ...route.snapshot.params };
    let currentRoute = route.parent;

    // Traverse up and accumulate params from parents
    while (currentRoute) {
      Object.assign(allParams, currentRoute.snapshot.params);
      currentRoute = currentRoute.parent;
    }

    return allParams;
  }
}
